/**
 * Treppan Academy - Exam Interface
 * MCQ exam with timer, navigation, and submission
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Clock, AlertCircle, ChevronLeft, ChevronRight, Flag,
  CheckCircle, Circle, GraduationCap, Award
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colors = {
  background: '#0A0D14',
  surface: '#131825',
  surfaceHover: '#1C2438',
  gold: '#D4AF37',
  success: '#10B981',
  fail: '#EF4444',
  warning: '#F59E0B',
  textHeading: '#FFFFFF',
  textBody: '#94A3B8',
  textMuted: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)'
};

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export default function ExamInterface() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);
  const [course, setCourse] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [examStarted, setExamStarted] = useState(false);

  useEffect(() => {
    fetchExam();
  }, [courseId]);

  useEffect(() => {
    if (!examStarted || timeLeft <= 0) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit(true); // Auto-submit on timeout
          return 0;
        }
        
        // Show warning at 5 minutes and 1 minute
        if (prev === 300) {
          toast.warning('5 minutes remaining!');
          setShowTimeWarning(true);
        } else if (prev === 60) {
          toast.error('1 minute remaining!');
        }
        
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [examStarted, timeLeft]);

  const fetchExam = async () => {
    try {
      const [examRes, courseRes] = await Promise.all([
        fetch(`${API_URL}/api/academy/courses/${courseId}/exam?token=${token}`),
        fetch(`${API_URL}/api/academy/courses/${courseId}?token=${token}`)
      ]);
      
      const examResult = await examRes.json();
      const courseResult = await courseRes.json();
      
      if (examResult.success && courseResult.success) {
        setExam(examResult.exam);
        setCourse(courseResult.course);
        setTimeLeft(examResult.exam.time_limit_minutes * 60);
      } else {
        toast.error('Exam not available');
        navigate(`/academy/courses/${courseId}`);
      }
    } catch (error) {
      console.error('Error fetching exam:', error);
      toast.error('Failed to load exam');
    } finally {
      setLoading(false);
    }
  };

  const handleStartExam = () => {
    setExamStarted(true);
    toast.info(`You have ${exam.time_limit_minutes} minutes to complete this exam.`);
  };

  const handleAnswerSelect = (questionId, optionIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: optionIndex
    }));
  };

  const toggleFlag = (questionId) => {
    setFlagged(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleSubmit = async (isTimeout = false) => {
    setSubmitting(true);
    setShowConfirmSubmit(false);
    
    try {
      const res = await fetch(
        `${API_URL}/api/academy/courses/${courseId}/exam/submit?token=${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers })
        }
      );
      
      const result = await res.json();
      
      if (result.success) {
        // Navigate to results page with result data
        navigate(`/academy/courses/${courseId}/result`, { 
          state: { result: result.result }
        });
      } else {
        toast.error('Failed to submit exam');
      }
    } catch (error) {
      console.error('Error submitting exam:', error);
      toast.error('Failed to submit exam');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <Award className="h-12 w-12 mx-auto mb-4 animate-pulse" style={{ color: colors.gold }} />
          <p style={{ color: colors.textBody }}>Loading exam...</p>
        </div>
      </div>
    );
  }

  if (!exam) return null;

  const questions = exam.questions || [];
  const answeredCount = Object.keys(answers).length;
  const progressPercent = Math.round((answeredCount / questions.length) * 100);
  const currentQ = questions[currentQuestion];
  const isTimeLow = timeLeft < 300;

  // Pre-exam screen
  if (!examStarted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: colors.background }}>
        <div 
          className="max-w-lg w-full rounded-xl p-8 text-center"
          style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <div 
            className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{ backgroundColor: `${colors.gold}20` }}
          >
            <Award className="h-10 w-10" style={{ color: colors.gold }} />
          </div>
          
          <h1 
            className="text-2xl font-bold mb-2"
            style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
          >
            {exam.title}
          </h1>
          
          <p className="mb-6" style={{ color: colors.textBody }}>
            {exam.description || `Certification exam for ${course?.title}`}
          </p>
          
          <div 
            className="grid grid-cols-2 gap-4 p-4 rounded-lg mb-6"
            style={{ backgroundColor: colors.background }}
          >
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: colors.gold }}>{questions.length}</p>
              <p className="text-xs uppercase tracking-widest" style={{ color: colors.textMuted }}>Questions</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: colors.gold }}>{exam.time_limit_minutes}</p>
              <p className="text-xs uppercase tracking-widest" style={{ color: colors.textMuted }}>Minutes</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: colors.success }}>{exam.passing_score}%</p>
              <p className="text-xs uppercase tracking-widest" style={{ color: colors.textMuted }}>To Pass</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: colors.textHeading }}>∞</p>
              <p className="text-xs uppercase tracking-widest" style={{ color: colors.textMuted }}>Attempts</p>
            </div>
          </div>
          
          <div 
            className="flex items-start gap-3 p-4 rounded-lg mb-6 text-left"
            style={{ backgroundColor: `${colors.warning}10`, border: `1px solid ${colors.warning}30` }}
          >
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: colors.warning }} />
            <div className="text-sm" style={{ color: colors.textBody }}>
              <p className="font-medium mb-1" style={{ color: colors.warning }}>Before you begin:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Timer starts once you click "Start Exam"</li>
                <li>You cannot pause or restart the exam</li>
                <li>All questions must be answered to submit</li>
              </ul>
            </div>
          </div>
          
          <div className="flex gap-4">
            <Button 
              variant="outline"
              className="flex-1"
              onClick={() => navigate(`/academy/courses/${courseId}`)}
              style={{ borderColor: colors.border, color: colors.textBody }}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Course
            </Button>
            <Button 
              className="flex-1 rounded-full font-semibold"
              onClick={handleStartExam}
              style={{ backgroundColor: colors.gold, color: '#000' }}
              data-testid="start-exam-btn"
            >
              Start Exam
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 border-b"
        style={{ backgroundColor: colors.surface, borderColor: colors.border }}
      >
        <div className="flex items-center gap-4">
          <GraduationCap className="h-6 w-6" style={{ color: colors.gold }} />
          <div>
            <h1 
              className="font-semibold"
              style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
            >
              {exam.title}
            </h1>
            <p className="text-sm" style={{ color: colors.textMuted }}>
              Question {currentQuestion + 1} of {questions.length}
            </p>
          </div>
        </div>
        
        {/* Timer */}
        <div 
          className={`flex items-center gap-2 px-4 py-2 rounded-full ${isTimeLow ? 'animate-pulse' : ''}`}
          style={{ 
            backgroundColor: isTimeLow ? `${colors.fail}20` : `${colors.gold}20`,
            border: `1px solid ${isTimeLow ? colors.fail : colors.gold}30`
          }}
        >
          <Clock className="h-4 w-4" style={{ color: isTimeLow ? colors.fail : colors.gold }} />
          <span 
            className="font-mono font-bold"
            style={{ color: isTimeLow ? colors.fail : colors.gold }}
          >
            {formatTime(timeLeft)}
          </span>
        </div>
      </div>

      {/* Progress */}
      <div className="px-4 py-2" style={{ backgroundColor: colors.surface }}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs" style={{ color: colors.textMuted }}>
            {answeredCount} of {questions.length} answered
          </span>
          <span className="text-xs" style={{ color: colors.gold }}>
            {progressPercent}%
          </span>
        </div>
        <Progress 
          value={progressPercent}
          className="h-1"
          style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Question Panel */}
        <div className="flex-1 p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-3xl mx-auto">
            {/* Question */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Badge 
                  className="uppercase text-xs tracking-widest"
                  style={{ 
                    backgroundColor: `${colors.gold}20`,
                    color: colors.gold
                  }}
                >
                  Question {currentQuestion + 1}
                </Badge>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => toggleFlag(currentQ.id)}
                  style={{ 
                    color: flagged.has(currentQ.id) ? colors.warning : colors.textMuted 
                  }}
                >
                  <Flag className="h-4 w-4 mr-1" />
                  {flagged.has(currentQ.id) ? 'Flagged' : 'Flag'}
                </Button>
              </div>
              
              <h2 
                className="text-xl font-semibold mb-6"
                style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
              >
                {currentQ.question}
              </h2>
              
              {/* Options */}
              <div className="space-y-3">
                {currentQ.options.map((option, index) => {
                  const isSelected = answers[currentQ.id] === index;
                  
                  return (
                    <div 
                      key={index}
                      className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all ${
                        isSelected ? 'ring-2' : 'hover:bg-white/5'
                      }`}
                      style={{ 
                        backgroundColor: isSelected ? `${colors.gold}10` : colors.surface,
                        borderColor: isSelected ? colors.gold : colors.border,
                        border: `1px solid ${isSelected ? colors.gold : colors.border}`
                      }}
                      onClick={() => handleAnswerSelect(currentQ.id, index)}
                      data-testid={`option-${index}`}
                    >
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ 
                          backgroundColor: isSelected ? colors.gold : 'transparent',
                          border: `2px solid ${isSelected ? colors.gold : colors.border}`
                        }}
                      >
                        {isSelected ? (
                          <CheckCircle className="h-5 w-5" style={{ color: '#000' }} />
                        ) : (
                          <span className="text-sm" style={{ color: colors.textMuted }}>
                            {String.fromCharCode(65 + index)}
                          </span>
                        )}
                      </div>
                      <span style={{ color: isSelected ? colors.textHeading : colors.textBody }}>
                        {option}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Question Navigation Panel */}
        <div 
          className="hidden lg:block w-64 border-l p-4 overflow-y-auto"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}
        >
          <h3 
            className="text-xs uppercase tracking-widest mb-4"
            style={{ color: colors.textMuted }}
          >
            Question Navigator
          </h3>
          
          <div className="grid grid-cols-5 gap-2">
            {questions.map((q, index) => {
              const isAnswered = answers[q.id] !== undefined;
              const isFlagged = flagged.has(q.id);
              const isCurrent = index === currentQuestion;
              
              return (
                <button 
                  key={q.id}
                  className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                    isCurrent ? 'ring-2 ring-offset-2 ring-offset-slate-900' : ''
                  }`}
                  style={{ 
                    backgroundColor: isAnswered ? colors.success : isFlagged ? colors.warning : colors.surfaceHover,
                    color: isAnswered || isFlagged ? '#000' : colors.textMuted,
                    ringColor: colors.gold
                  }}
                  onClick={() => setCurrentQuestion(index)}
                  data-testid={`nav-q-${index}`}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
          
          <div className="mt-6 space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: colors.success }} />
              <span style={{ color: colors.textMuted }}>Answered</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: colors.warning }} />
              <span style={{ color: colors.textMuted }}>Flagged</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: colors.surfaceHover }} />
              <span style={{ color: colors.textMuted }}>Unanswered</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div 
        className="flex items-center justify-between p-4 border-t"
        style={{ backgroundColor: colors.surface, borderColor: colors.border }}
      >
        <Button 
          variant="outline"
          onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
          disabled={currentQuestion === 0}
          style={{ borderColor: colors.border, color: colors.textBody }}
          data-testid="prev-question-btn"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>
        
        <Button 
          className="rounded-full font-semibold px-8"
          onClick={() => setShowConfirmSubmit(true)}
          disabled={submitting}
          style={{ backgroundColor: colors.gold, color: '#000' }}
          data-testid="submit-exam-btn"
        >
          {submitting ? 'Submitting...' : 'Submit Exam'}
        </Button>
        
        <Button 
          onClick={() => setCurrentQuestion(prev => Math.min(questions.length - 1, prev + 1))}
          disabled={currentQuestion === questions.length - 1}
          style={{ backgroundColor: colors.surfaceHover, color: colors.textHeading }}
          data-testid="next-question-btn"
        >
          Next
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      {/* Confirm Submit Dialog */}
      <AlertDialog open={showConfirmSubmit} onOpenChange={setShowConfirmSubmit}>
        <AlertDialogContent style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: colors.textHeading }}>
              Submit Exam?
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: colors.textBody }}>
              {answeredCount < questions.length ? (
                <span className="text-yellow-500">
                  You have {questions.length - answeredCount} unanswered question(s). 
                  Are you sure you want to submit?
                </span>
              ) : (
                'You have answered all questions. Ready to submit?'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderColor: colors.border, color: colors.textBody }}>
              Continue Exam
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleSubmit(false)}
              style={{ backgroundColor: colors.gold, color: '#000' }}
            >
              Submit Exam
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
